import Phaser from "phaser";
import type { EquipmentSlots } from "../core/types";
import { useRealAssets } from "../config";
import {
  Characters,
  DIRECTIONS,
  characterSheetKey,
  type CharState,
  type Direction,
  type SheetDef,
} from "./AssetManifest";
import * as P from "../art/props";

/**
 * REPRESENTACIÓN de un personaje (EPI o NPC), desacoplada de su lógica.
 *
 *   spritesheet real disponible → Phaser.Sprite con animaciones por dirección
 *   sin sheet                   → placeholder vectorial (art/props.ts)
 *
 * API estable para el resto del juego:
 *   setDirection("SW"); setState("walk"); applyEquipment(slots); update(dt, moving)
 *
 * El origin es la BASE/pies; la sombra es un sprite SEPARADO (ver ART_SPEC §7).
 * No contiene lógica de movimiento: solo dibuja lo que le dicen.
 */
export class CharacterVisual {
  readonly container: Phaser.GameObjects.Container;

  private sprite?: Phaser.GameObjects.Sprite;
  private vector?: Phaser.GameObjects.Container;
  private equipLayers: Partial<Record<keyof EquipmentSlots, Phaser.GameObjects.Shape[]>> = {};

  private dir: Direction = "SW";
  private state: CharState = "idle";
  private facing = 1;
  private bob = 0;
  private readonly usesSprite: boolean;

  constructor(private scene: Phaser.Scene, private charKey: string) {
    const shadow = P.shadow(scene, 26, 12); // sombra separada (personajes)
    this.usesSprite = this.spriteAvailable();

    if (this.usesSprite) {
      CharacterVisual.ensureAnimations(scene, charKey);
      this.sprite = scene.add.sprite(0, 0, characterSheetKey(charKey, "idle")).setOrigin(0.5, 1);
      this.container = scene.add.container(0, 0, [shadow, this.sprite]);
      this.play();
    } else {
      this.vector = this.buildVector();
      this.container = scene.add.container(0, 0, [shadow, this.vector]);
    }
  }

  private spriteAvailable(): boolean {
    if (!useRealAssets()) return false;
    return this.scene.textures.exists(characterSheetKey(this.charKey, "idle"));
  }

  // ---- API pública ----

  setDirection(dir: Direction): void {
    if (dir === this.dir) return;
    this.dir = dir;
    this.facing = dir === "NE" || dir === "SE" ? 1 : -1; // E mira derecha, W izquierda
    this.play();
  }

  setState(state: CharState): void {
    if (state === this.state) return;
    this.state = state;
    this.play();
  }

  /** Solo aplica a EPI vectorial: muestra/oculta las capas de EPI equipados. */
  applyEquipment(slots: EquipmentSlots): void {
    if (this.usesSprite) return; // (con sprite real, el equipo será parte del arte)
    (Object.keys(this.equipLayers) as (keyof EquipmentSlots)[]).forEach((slot) => {
      const on = slots[slot] != null;
      this.equipLayers[slot]?.forEach((o) => o.setVisible(on));
    });
  }

  update(_dt: number, moving: boolean): void {
    if (this.usesSprite) return; // las animaciones las gestiona Phaser
    // placeholder vectorial: rebote al caminar + giro E/O
    this.bob = moving ? this.bob + 0.35 : 0;
    if (this.vector) {
      this.vector.setY(Math.sin(this.bob) * -2.2);
      this.vector.setScale(this.facing, 1);
    }
  }

  destroy(): void {
    this.container.destroy();
  }

  // ---- interno ----

  private play(): void {
    if (!this.usesSprite || !this.sprite) return;
    const key = `${this.charKey}_${this.state}_${this.dir}`;
    if (this.scene.anims.exists(key)) { this.sprite.play(key, true); return; }
    // degradación: si no existe la animación del estado (p. ej. walk sin hoja),
    // usa la de idle en la MISMA dirección (el personaje al menos mira bien).
    const idleKey = `${this.charKey}_idle_${this.dir}`;
    if (this.scene.anims.exists(idleKey)) this.sprite.play(idleKey, true);
  }

  private buildVector(): Phaser.GameObjects.Container {
    const fallback = Characters[this.charKey]?.fallback ?? this.charKey;
    if (fallback === "epi") return this.buildEpiVector();
    // NPC vectoriales por preset
    const presets: Record<string, Parameters<typeof P.worker>[1]> = {
      workerAntonio: { shirt: 0xb06a3a, hair: 0x5a3a24 },
      workerLucia: { shirt: 0x2f7d78, vest: 0xe6d020, hair: 0x3a2a20 },
      workerManolo: { shirt: 0x5a5f68, helmet: 0xf0c400, hair: 0x8a8a90 },
    };
    return P.worker(this.scene, presets[fallback] ?? {});
  }

  /** EPI vectorial PROPIO: identidad de base (barba, ropa petróleo, identificador,
   *  libreta) + capas de EPI equipables (casco, chaleco, botas, arnés, gafas...). */
  private buildEpiVector(): Phaser.GameObjects.Container {
    const s = this.scene;
    const fig = s.add.container(0, 0);
    const R = (x: number, y: number, w: number, h: number, c: number) => s.add.rectangle(x, y, w, h, c).setOrigin(0.5, 1);
    const add = (...o: Phaser.GameObjects.GameObject[]) => fig.add(o);

    // --- BASE: EPI sin EPIs, pero reconocible ---
    add(R(-6, 0, 10, 22, 0x2b3550), R(6, 0, 10, 22, 0x2b3550), R(6, 0, 4, 22, 0x212a42)); // piernas
    add(s.add.ellipse(-6, -1, 13, 6, 0x241d14), s.add.ellipse(6, -1, 13, 6, 0x241d14));    // zapatos base
    add(R(0, -18, 28, 28, 0x2c5a66).setStrokeStyle(2, 0x1c3b44), R(8, -18, 12, 28, 0x235059)); // camisa petróleo
    add(R(0, -18, 28, 5, 0x241d14), s.add.rectangle(0, -20, 6, 4, 0xb8a24a));                // cinturón
    add(R(-15, -20, 8, 22, 0x2c5a66), R(15, -20, 8, 22, 0x235059));                          // brazos
    add(s.add.circle(-15, -22, 4, 0xe8b48c), s.add.circle(15, -22, 4, 0xe8b48c));            // manos
    add(R(0, -42, 9, 6, 0xdca77e));                                                          // cuello
    add(s.add.circle(0, -52, 11, 0xe8b48c));                                                 // cabeza
    add(s.add.arc(0, -50, 11, 20, 160, false, 0x4a3826).setOrigin(0.5, 0.5));                // barba
    add(s.add.arc(0, -56, 12, 180, 360, false, 0x3a2c1e));                                   // pelo
    add(s.add.ellipse(-4, -53, 2.4, 3, 0x241d14), s.add.ellipse(4, -53, 2.4, 3, 0x241d14));  // ojos
    add(s.add.rectangle(-6, -57, 5, 1.6, 0x2a1f16), s.add.rectangle(6, -57, 5, 1.6, 0x2a1f16)); // cejas
    add(s.add.ellipse(0, -49, 2.5, 2, 0xcf9a72));                                            // nariz
    // identificador colgante
    add(s.add.rectangle(2, -34, 2, 8, 0x9aa0a8).setOrigin(0.5, 1));
    add(s.add.rectangle(2, -26, 9, 11, 0xf4ead0).setOrigin(0.5, 1).setStrokeStyle(1, 0x8a9098));
    add(s.add.rectangle(2, -30, 6, 3, 0x2c6270).setOrigin(0.5, 1));
    // libreta al cinturón
    add(s.add.rectangle(-11, -14, 8, 11, 0x9a744a).setOrigin(0.5, 1).setStrokeStyle(1, 0x6a4f2c));
    add(s.add.rectangle(-11, -20, 8, 2, 0xd8b93f).setOrigin(0.5, 1));

    // --- CAPAS DE EPI (ocultas hasta equipar) ---
    const layer = (slot: keyof EquipmentSlots, objs: Phaser.GameObjects.GameObject[]) => {
      fig.add(objs);
      this.equipLayers[slot] = objs as unknown as Phaser.GameObjects.Shape[];
    };
    layer("feet", [
      R(-6, 0, 15, 9, 0x3a2a18).setStrokeStyle(1, 0x241206),
      R(6, 0, 15, 9, 0x3a2a18).setStrokeStyle(1, 0x241206),
      s.add.ellipse(-9, -1, 7, 4, 0xb8b8c0), s.add.ellipse(9, -1, 7, 4, 0xb8b8c0),
    ]);
    layer("torso", [
      R(0, -18, 30, 27, 0xe9b70f).setStrokeStyle(2, 0xb08c0a),
      R(9, -18, 12, 27, 0xcf9f0a),
      R(0, -30, 30, 3.5, 0xdfe4e6), R(0, -20, 30, 3.5, 0xdfe4e6),
      s.add.rectangle(-9, -32, 4, 16, 0xdfe4e6).setOrigin(0.5, 1),
      s.add.rectangle(9, -32, 4, 16, 0xdfe4e6).setOrigin(0.5, 1),
    ]);
    layer("fall", [
      s.add.rectangle(-7, -30, 3.5, 26, 0x2f5a8a).setOrigin(0.5, 1),
      s.add.rectangle(7, -30, 3.5, 26, 0x2f5a8a).setOrigin(0.5, 1),
      R(0, -22, 30, 4, 0x2f5a8a),
      s.add.circle(0, -28, 3, 0xc9c9cf),
    ]);
    layer("respiratory", [
      s.add.ellipse(0, -47, 17, 11, 0xdfe4e6).setStrokeStyle(1, 0x9aa4b0),
      s.add.circle(-5, -46, 2, 0xb8b8c0), s.add.circle(5, -46, 2, 0xb8b8c0),
    ]);
    layer("eyes", [
      s.add.rectangle(0, -53, 20, 7, 0x2b2f35).setOrigin(0.5, 0.5),
      s.add.ellipse(-5, -53, 6, 5, 0x8ac6d0), s.add.ellipse(5, -53, 6, 5, 0x8ac6d0),
    ]);
    layer("ears", [
      s.add.rectangle(0, -57, 22, 4, 0x1c6b64).setOrigin(0.5, 0.5),
      s.add.circle(-11, -53, 5, 0x1c6b64), s.add.circle(11, -53, 5, 0x1c6b64),
      s.add.circle(-11, -53, 2.5, 0x0f453f), s.add.circle(11, -53, 2.5, 0x0f453f),
    ]);
    layer("head", [
      s.add.arc(0, -58, 13, 180, 360, false, 0xf0c400).setStrokeStyle(2, 0xc9a10a),
      s.add.arc(0, -58, 13, 200, 340, false, 0xf5cf3a),
      s.add.rectangle(0, -58, 30, 5, 0xf0c400).setOrigin(0.5, 0.5).setStrokeStyle(1, 0xc9a10a),
      s.add.rectangle(0, -63, 12, 4, 0x2b2b30).setOrigin(0.5, 0.5),
    ]);
    for (const objs of Object.values(this.equipLayers)) objs?.forEach((o) => o.setVisible(false));
    return fig;
  }

  // ---- animaciones (se crean una vez por escena si hay textura) ----

  static ensureAnimations(scene: Phaser.Scene, charKey: string): void {
    const def = Characters[charKey];
    if (!def) return;
    const build = (state: CharState, sheet: SheetDef) => {
      const texKey = characterSheetKey(charKey, state);
      if (!scene.textures.exists(texKey)) return;
      DIRECTIONS.forEach((dir, i) => {
        const animKey = `${charKey}_${state}_${dir}`;
        if (scene.anims.exists(animKey)) return;
        const start = i * sheet.framesPerDir;
        const end = start + sheet.framesPerDir - 1;
        scene.anims.create({
          key: animKey,
          frames: scene.anims.generateFrameNumbers(texKey, { start, end }),
          frameRate: sheet.frameRate,
          repeat: -1,
        });
      });
    };
    build("idle", def.idle);
    build("walk", def.walk);
  }

  /** Encola en el preload los spritesheets de personaje (si están activados). */
  static preload(scene: Phaser.Scene): void {
    if (!useRealAssets()) return;
    for (const [charKey, def] of Object.entries(Characters)) {
      const load = (state: CharState, sheet: SheetDef) => {
        if (!sheet.available) return; // solo hojas ya producidas
        scene.load.spritesheet(characterSheetKey(charKey, state), sheet.path, {
          frameWidth: sheet.frameWidth,
          frameHeight: sheet.frameHeight,
        });
      };
      load("idle", def.idle);
      load("walk", def.walk);
    }
    scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {
      /* silencioso: si falta el sheet, se usa el placeholder vectorial */
    });
  }
}
